import {
  Dispatch,
  FC,
  ReactNode,
  createContext,
  useContext,
  useReducer,
  useMemo,
} from 'react';
import {
  THealthIncident,
  TSafetyProtocol,
  TEmergencyContact,
  THealthCheckup,
} from 'src/apis/administration/administration.interfaces';

type TEditMode = 'create' | 'edit' | 'view';

export enum EViewState {
  LIST = 'list',
  DETAILS = 'details',
  EDIT = 'edit',
  ADD = 'add',
}

export type THealthAndSafetyViewType =
  | 'incidents'
  | 'protocols'
  | 'emergencyContacts'
  | 'checkups';

type THealthAndSafetyState = {
  incidents: THealthIncident[];
  protocols: TSafetyProtocol[];
  emergencyContacts: TEmergencyContact[];
  healthCheckups: THealthCheckup[];
  selectedIncident?: THealthIncident;
  selectedProtocol?: TSafetyProtocol;
  selectedContact?: TEmergencyContact;
  selectedCheckup?: THealthCheckup;
  filteredIncidents: THealthIncident[];
  filteredProtocols: TSafetyProtocol[];
  searchTerm: string;
  selectedCategoryFilter: string;
  isLoading: boolean;
  errorMessage?: string;
  editMode?: TEditMode;
  viewState: EViewState;
  currentViewType?: THealthAndSafetyViewType;
};

export enum ACTION_TYPE {
  SET_INCIDENTS = 'SET_INCIDENTS',
  SET_PROTOCOLS = 'SET_PROTOCOLS',
  SET_EMERGENCY_CONTACTS = 'SET_EMERGENCY_CONTACTS',
  SET_HEALTH_CHECKUPS = 'SET_HEALTH_CHECKUPS',
  SELECT_INCIDENT = 'SELECT_INCIDENT',
  SELECT_PROTOCOL = 'SELECT_PROTOCOL',
  SELECT_CONTACT = 'SELECT_CONTACT',
  SELECT_CHECKUP = 'SELECT_CHECKUP',
  ADD_INCIDENT = 'ADD_INCIDENT',
  UPDATE_INCIDENT = 'UPDATE_INCIDENT',
  DELETE_INCIDENT = 'DELETE_INCIDENT',
  ADD_PROTOCOL = 'ADD_PROTOCOL',
  UPDATE_PROTOCOL = 'UPDATE_PROTOCOL',
  DELETE_PROTOCOL = 'DELETE_PROTOCOL',
  ADD_CONTACT = 'ADD_CONTACT',
  UPDATE_CONTACT = 'UPDATE_CONTACT',
  DELETE_CONTACT = 'DELETE_CONTACT',
  ADD_CHECKUP = 'ADD_CHECKUP',
  UPDATE_CHECKUP = 'UPDATE_CHECKUP',
  DELETE_CHECKUP = 'DELETE_CHECKUP',
  SET_SEARCH_TERM = 'SET_SEARCH_TERM',
  SET_CATEGORY_FILTER = 'SET_CATEGORY_FILTER',
  FILTER_INCIDENTS = 'FILTER_INCIDENTS',
  FILTER_PROTOCOLS = 'FILTER_PROTOCOLS',
  SET_LOADING = 'SET_LOADING',
  SET_ERROR = 'SET_ERROR',
  SET_EDIT_MODE = 'SET_EDIT_MODE',
  SET_VIEW_STATE = 'SET_VIEW_STATE',
  SET_VIEW_TYPE = 'SET_VIEW_TYPE',
}

type Action =
  | { type: ACTION_TYPE.SET_INCIDENTS; payload: THealthIncident[] }
  | { type: ACTION_TYPE.SET_PROTOCOLS; payload: TSafetyProtocol[] }
  | { type: ACTION_TYPE.SET_EMERGENCY_CONTACTS; payload: TEmergencyContact[] }
  | { type: ACTION_TYPE.SET_HEALTH_CHECKUPS; payload: THealthCheckup[] }
  | { type: ACTION_TYPE.SELECT_INCIDENT; payload: THealthIncident | undefined }
  | { type: ACTION_TYPE.SELECT_PROTOCOL; payload: TSafetyProtocol | undefined }
  | { type: ACTION_TYPE.SELECT_CONTACT; payload: TEmergencyContact | undefined }
  | { type: ACTION_TYPE.SELECT_CHECKUP; payload: THealthCheckup | undefined }
  | { type: ACTION_TYPE.ADD_INCIDENT; payload: THealthIncident }
  | { type: ACTION_TYPE.UPDATE_INCIDENT; payload: THealthIncident }
  | { type: ACTION_TYPE.DELETE_INCIDENT; payload: string }
  | { type: ACTION_TYPE.ADD_PROTOCOL; payload: TSafetyProtocol }
  | { type: ACTION_TYPE.UPDATE_PROTOCOL; payload: TSafetyProtocol }
  | { type: ACTION_TYPE.DELETE_PROTOCOL; payload: string }
  | { type: ACTION_TYPE.ADD_CONTACT; payload: TEmergencyContact }
  | { type: ACTION_TYPE.UPDATE_CONTACT; payload: TEmergencyContact }
  | { type: ACTION_TYPE.DELETE_CONTACT; payload: string }
  | { type: ACTION_TYPE.ADD_CHECKUP; payload: THealthCheckup }
  | { type: ACTION_TYPE.UPDATE_CHECKUP; payload: THealthCheckup }
  | { type: ACTION_TYPE.DELETE_CHECKUP; payload: string }
  | { type: ACTION_TYPE.SET_SEARCH_TERM; payload: string }
  | { type: ACTION_TYPE.SET_CATEGORY_FILTER; payload: string }
  | { type: ACTION_TYPE.FILTER_INCIDENTS; payload: THealthIncident[] }
  | { type: ACTION_TYPE.FILTER_PROTOCOLS; payload: TSafetyProtocol[] }
  | { type: ACTION_TYPE.SET_LOADING; payload: boolean }
  | { type: ACTION_TYPE.SET_ERROR; payload: string | undefined }
  | { type: ACTION_TYPE.SET_EDIT_MODE; payload: TEditMode | undefined }
  | { type: ACTION_TYPE.SET_VIEW_STATE; payload: EViewState }
  | {
      type: ACTION_TYPE.SET_VIEW_TYPE;
      payload: THealthAndSafetyViewType | undefined;
    };

const initialState: THealthAndSafetyState = {
  incidents: [],
  protocols: [],
  emergencyContacts: [],
  healthCheckups: [],
  selectedIncident: undefined,
  selectedProtocol: undefined,
  selectedContact: undefined,
  selectedCheckup: undefined,
  filteredIncidents: [],
  filteredProtocols: [],
  searchTerm: '',
  selectedCategoryFilter: '',
  isLoading: false,
  errorMessage: undefined,
  editMode: undefined,
  viewState: EViewState.LIST,
  currentViewType: undefined,
};

const HealthAndSafetyContext = createContext<THealthAndSafetyState | undefined>(
  undefined
);
const HealthAndSafetyDispatchContext = createContext<
  Dispatch<Action> | undefined
>(undefined);

const healthAndSafetyReducer = (
  state: THealthAndSafetyState,
  action: Action
): THealthAndSafetyState => {
  switch (action.type) {
    case ACTION_TYPE.SET_INCIDENTS:
      return {
        ...state,
        incidents: action.payload,
        filteredIncidents: action.payload,
      };
    case ACTION_TYPE.SET_PROTOCOLS:
      return {
        ...state,
        protocols: action.payload,
        filteredProtocols: action.payload,
      };
    case ACTION_TYPE.SET_EMERGENCY_CONTACTS:
      return { ...state, emergencyContacts: action.payload };
    case ACTION_TYPE.SET_HEALTH_CHECKUPS:
      return { ...state, healthCheckups: action.payload };
    case ACTION_TYPE.SELECT_INCIDENT:
      return { ...state, selectedIncident: action.payload };
    case ACTION_TYPE.SELECT_PROTOCOL:
      return { ...state, selectedProtocol: action.payload };
    case ACTION_TYPE.SELECT_CONTACT:
      return { ...state, selectedContact: action.payload };
    case ACTION_TYPE.SELECT_CHECKUP:
      return { ...state, selectedCheckup: action.payload };
    case ACTION_TYPE.ADD_INCIDENT:
      return {
        ...state,
        incidents: [...state.incidents, action.payload],
        filteredIncidents: [...state.filteredIncidents, action.payload],
      };
    case ACTION_TYPE.UPDATE_INCIDENT:
      return {
        ...state,
        incidents: state.incidents.map((incident) =>
          incident.id === action.payload.id ? action.payload : incident
        ),
        filteredIncidents: state.filteredIncidents.map((incident) =>
          incident.id === action.payload.id ? action.payload : incident
        ),
      };
    case ACTION_TYPE.DELETE_INCIDENT:
      return {
        ...state,
        incidents: state.incidents.filter(
          (incident) => incident.id !== action.payload
        ),
        filteredIncidents: state.filteredIncidents.filter(
          (incident) => incident.id !== action.payload
        ),
      };
    case ACTION_TYPE.ADD_PROTOCOL:
      return {
        ...state,
        protocols: [...state.protocols, action.payload],
        filteredProtocols: [...state.filteredProtocols, action.payload],
      };
    case ACTION_TYPE.UPDATE_PROTOCOL:
      return {
        ...state,
        protocols: state.protocols.map((protocol) =>
          protocol.id === action.payload.id ? action.payload : protocol
        ),
        filteredProtocols: state.filteredProtocols.map((protocol) =>
          protocol.id === action.payload.id ? action.payload : protocol
        ),
      };
    case ACTION_TYPE.DELETE_PROTOCOL:
      return {
        ...state,
        protocols: state.protocols.filter(
          (protocol) => protocol.id !== action.payload
        ),
        filteredProtocols: state.filteredProtocols.filter(
          (protocol) => protocol.id !== action.payload
        ),
      };
    case ACTION_TYPE.ADD_CONTACT:
      return {
        ...state,
        emergencyContacts: [...state.emergencyContacts, action.payload],
      };
    case ACTION_TYPE.UPDATE_CONTACT:
      return {
        ...state,
        emergencyContacts: state.emergencyContacts.map((contact) =>
          contact.id === action.payload.id ? action.payload : contact
        ),
      };
    case ACTION_TYPE.DELETE_CONTACT:
      return {
        ...state,
        emergencyContacts: state.emergencyContacts.filter(
          (contact) => contact.id !== action.payload
        ),
      };
    case ACTION_TYPE.ADD_CHECKUP:
      return {
        ...state,
        healthCheckups: [...state.healthCheckups, action.payload],
      };
    case ACTION_TYPE.UPDATE_CHECKUP:
      return {
        ...state,
        healthCheckups: state.healthCheckups.map((checkup) =>
          checkup.id === action.payload.id ? action.payload : checkup
        ),
      };
    case ACTION_TYPE.DELETE_CHECKUP:
      return {
        ...state,
        healthCheckups: state.healthCheckups.filter(
          (checkup) => checkup.id !== action.payload
        ),
      };
    case ACTION_TYPE.SET_SEARCH_TERM:
      return { ...state, searchTerm: action.payload };
    case ACTION_TYPE.SET_CATEGORY_FILTER:
      return { ...state, selectedCategoryFilter: action.payload };
    case ACTION_TYPE.FILTER_INCIDENTS:
      return { ...state, filteredIncidents: action.payload };
    case ACTION_TYPE.FILTER_PROTOCOLS:
      return { ...state, filteredProtocols: action.payload };
    case ACTION_TYPE.SET_LOADING:
      return { ...state, isLoading: action.payload };
    case ACTION_TYPE.SET_ERROR:
      return { ...state, errorMessage: action.payload };
    case ACTION_TYPE.SET_EDIT_MODE:
      return { ...state, editMode: action.payload };
    case ACTION_TYPE.SET_VIEW_STATE:
      return { ...state, viewState: action.payload };
    case ACTION_TYPE.SET_VIEW_TYPE:
      return { ...state, currentViewType: action.payload };
    default:
      throw new Error('Unhandled action type');
  }
};

export const HealthAndSafetyProvider: FC<{ children: ReactNode }> = ({
  children,
}) => {
  const [state, dispatch] = useReducer(healthAndSafetyReducer, initialState);

  const contextValue = useMemo(() => state, [state]);

  return (
    <div data-testid="health-safety-provider">
      <HealthAndSafetyContext.Provider value={contextValue}>
        <HealthAndSafetyDispatchContext.Provider value={dispatch}>
          {children}
        </HealthAndSafetyDispatchContext.Provider>
      </HealthAndSafetyContext.Provider>
    </div>
  );
};

export const useHealthAndSafetyState = () => {
  const context = useContext(HealthAndSafetyContext);
  if (context === undefined) {
    throw new Error(
      'useHealthAndSafetyState must be used within a HealthAndSafetyProvider'
    );
  }
  return context;
};

export const useHealthAndSafetyDispatch = () => {
  const context = useContext(HealthAndSafetyDispatchContext);
  if (context === undefined) {
    throw new Error(
      'useHealthAndSafetyDispatch must be used within a HealthAndSafetyProvider'
    );
  }
  return context;
};

export const useHealthAndSafetyActions = () => {
  const dispatch = useContext(HealthAndSafetyDispatchContext);
  if (dispatch === undefined) {
    throw new Error(
      'useHealthAndSafetyActions must be used within a HealthAndSafetyProvider'
    );
  }

  const setEditMode = (editMode: TEditMode | undefined) => {
    dispatch({ type: ACTION_TYPE.SET_EDIT_MODE, payload: editMode });
  };

  const setViewState = (viewState: EViewState) => {
    dispatch({ type: ACTION_TYPE.SET_VIEW_STATE, payload: viewState });
  };

  const setViewType = (viewType: THealthAndSafetyViewType | undefined) => {
    dispatch({ type: ACTION_TYPE.SET_VIEW_TYPE, payload: viewType });
  };

  const selectIncident = (incident: THealthIncident | undefined) => {
    dispatch({ type: ACTION_TYPE.SELECT_INCIDENT, payload: incident });
  };

  const selectProtocol = (protocol: TSafetyProtocol | undefined) => {
    dispatch({ type: ACTION_TYPE.SELECT_PROTOCOL, payload: protocol });
  };

  const selectContact = (contact: TEmergencyContact | undefined) => {
    dispatch({ type: ACTION_TYPE.SELECT_CONTACT, payload: contact });
  };

  const selectCheckup = (checkup: THealthCheckup | undefined) => {
    dispatch({ type: ACTION_TYPE.SELECT_CHECKUP, payload: checkup });
  };

  return {
    setEditMode,
    setViewState,
    setViewType,
    selectIncident,
    selectProtocol,
    selectContact,
    selectCheckup,
  };
};
